import { useState } from 'react'
import {
  SummaryValues as SummaryValuesDS,
  Skeleton,
  MonetizationIcon,
  Text,
  Select,
} from '@mercai/clever'
import { useTranslation } from 'react-i18next'

import { formatCurrency } from '@/helpers'

import styles from './card-order-summary-values.module.css'

import type { CardOrderSummaryValuesProps } from './card-order-summary-values.type'
import { CardInfo } from '@/components/card-info'

const CardOrderSummaryValuesDefault = ({
  totalFreight,
  iva,
  subTotal,
  total,
  discount,
  paymentForm,
  dollarExchangeRate,
  paymentMethod,
}: CardOrderSummaryValuesProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('componentCardsOrderCenterShow')

  const [currency, setCurrency] = useState<'en-US' | 'es-MX'>('en-US')

  const valuesInCurrency = {
    'es-MX': {
      subTotal: subTotal * +(dollarExchangeRate || 0),
      iva: iva * +(dollarExchangeRate || 0),
      total: total * +(dollarExchangeRate || 0),
      discount: discount * +(dollarExchangeRate || 0),
      totalFreight: totalFreight * +(dollarExchangeRate || 0),
    },
    'en-US': {
      subTotal,
      iva,
      total,
      discount,
      totalFreight,
    },
  }

  return (
    <CardInfo
      icon={<MonetizationIcon />}
      title={t('orderPaymentShowSummaryValuestitle')}
    >
      <div className={styles['info-item']}>
        <Text>{t('orderPaymentShowSummaryValuesValuesIn')}:</Text>
        <Select
          className={styles['custom-select']}
          size="small"
          options={[
            { label: 'USD', value: 'en-US' },
            { label: 'MX', value: 'es-MX' },
          ]}
          value={currency}
          onChange={(value) => setCurrency(value as 'en-US' | 'es-MX')}
        />
      </div>

      <div className={styles['info-item']}>
        <Text>{t('orderPaymentShowSummaryValuesDollaQuotation')}:</Text>
        <Text>
          <strong>1.00 USD = MX$ {dollarExchangeRate}</strong>
        </Text>
      </div>

      <div className={styles['info-item']}>
        <Text>{t('orderPaymentShowSummaryValuesPaymentForm')}:</Text>
        <Text>
          <strong>
            {paymentForm
              ? tEnums(`financial_profile_payment_forms_${paymentForm}`)
              : '-'}
          </strong>
        </Text>
      </div>

      <div className={styles['info-item']}>
        <Text>{t('orderPaymentShowSummaryValuesPaymentMethod')}:</Text>
        <Text>
          <strong>
            {paymentMethod
              ? tEnums(
                  `financial_profile_transaction_payment_methods_${paymentMethod}`,
                )
              : '-'}
          </strong>
        </Text>
      </div>

      <div className={styles.wrap}>
        <SummaryValuesDS.Root>
          <SummaryValuesDS.Row>
            <SummaryValuesDS.Column type="value">
              {t('orderPaymentShowSummaryValuesSubTotal')}
            </SummaryValuesDS.Column>
            <SummaryValuesDS.Column type="value" direction="right">
              {formatCurrency(
                valuesInCurrency[currency].subTotal,
                currency,
                currency === 'en-US' ? 'USD' : 'MXN',
              )}
            </SummaryValuesDS.Column>
          </SummaryValuesDS.Row>

          {!discount || (
            <SummaryValuesDS.Row>
              <SummaryValuesDS.Column type="value">
                {t('orderPaymentShowSummaryValuesDiscount')}
              </SummaryValuesDS.Column>
              <SummaryValuesDS.Column type="value" direction="right">
                -{' '}
                {formatCurrency(
                  valuesInCurrency[currency].discount,
                  currency,
                  currency === 'en-US' ? 'USD' : 'MXN',
                )}
              </SummaryValuesDS.Column>
            </SummaryValuesDS.Row>
          )}

          {iva > 0 && (
            <SummaryValuesDS.Row>
              <SummaryValuesDS.Column type="value">
                {t('orderPaymentShowSummaryValuesIVA')}
              </SummaryValuesDS.Column>
              <SummaryValuesDS.Column type="value" direction="right">
                {formatCurrency(
                  valuesInCurrency[currency].iva,
                  currency,
                  currency === 'en-US' ? 'USD' : 'MXN',
                )}
              </SummaryValuesDS.Column>
            </SummaryValuesDS.Row>
          )}

          <SummaryValuesDS.Row>
            <SummaryValuesDS.Column type="value">
              {t('orderPaymentShowSummaryValuesDelivery')}
            </SummaryValuesDS.Column>
            <SummaryValuesDS.Column type="value" direction="right">
              {formatCurrency(
                valuesInCurrency[currency].totalFreight,
                currency,
                currency === 'en-US' ? 'USD' : 'MXN',
              )}
            </SummaryValuesDS.Column>
          </SummaryValuesDS.Row>

          <SummaryValuesDS.Row type="strip" className={styles['summary-total']}>
            <SummaryValuesDS.Column type="bookend">
              {t('orderPaymentShowSummaryValuesAmount')}
            </SummaryValuesDS.Column>
            <SummaryValuesDS.Column type="bookend" direction="right">
              {formatCurrency(
                valuesInCurrency[currency].total,
                currency,
                currency === 'en-US' ? 'USD' : 'MXN',
              )}
            </SummaryValuesDS.Column>
          </SummaryValuesDS.Row>
        </SummaryValuesDS.Root>
      </div>
    </CardInfo>
  )
}

const CardOrderSummaryValuesLoading = () => {
  const { t } = useTranslation('componentCardsOrderCenterShow')
  return (
    <CardInfo
      icon={<MonetizationIcon />}
      title={t('orderPaymentShowSummaryValuestitle')}
    >
      <SummaryValuesDS.Root>
        <SummaryValuesDS.Row>
          <SummaryValuesDS.Column type="label">
            <Skeleton width="7rem" height="1rem" />
          </SummaryValuesDS.Column>
          <SummaryValuesDS.Column type="value" direction="right">
            <Skeleton width="6rem" height="1rem" />
          </SummaryValuesDS.Column>
        </SummaryValuesDS.Row>

        <SummaryValuesDS.Row>
          <SummaryValuesDS.Column type="label">
            <Skeleton width="7rem" height="1rem" />
          </SummaryValuesDS.Column>
          <SummaryValuesDS.Column type="value" direction="right">
            <Skeleton width="6rem" height="1rem" />
          </SummaryValuesDS.Column>
        </SummaryValuesDS.Row>

        <SummaryValuesDS.Row>
          <SummaryValuesDS.Column type="label">
            <Skeleton width="7rem" height="1rem" />
          </SummaryValuesDS.Column>
          <SummaryValuesDS.Column type="value" direction="right">
            <Skeleton width="6rem" height="1rem" />
          </SummaryValuesDS.Column>
        </SummaryValuesDS.Row>

        <SummaryValuesDS.Row type="strip">
          <SummaryValuesDS.Column type="bookend">
            <Skeleton width="5rem" height="1.6rem" />
          </SummaryValuesDS.Column>
          <SummaryValuesDS.Column type="bookend" direction="right">
            <Skeleton width="7rem" height="1.6rem" />
          </SummaryValuesDS.Column>
        </SummaryValuesDS.Row>
      </SummaryValuesDS.Root>
    </CardInfo>
  )
}

export const CardOrderSummaryValues = (props: CardOrderSummaryValuesProps) => {
  if (props.isLoading) {
    return <CardOrderSummaryValuesLoading />
  }

  return <CardOrderSummaryValuesDefault {...props} />
}
